/**
 * Session Storage Utility
 * Centralized management of session-based token storage
 */

const TOKEN_KEYS = {
    ID_TOKEN: 'idToken',
    ACCESS_TOKEN: 'accessToken',
    REFRESH_TOKEN: 'refreshToken',
} as const;

/**
 * Store a token in sessionStorage
 */
export const setSessionToken = (key: string, value: string): void => {
    try {
        sessionStorage.setItem(key, value);
    } catch (error) {
        console.error('[Session Storage] Failed to set token:', error);
    }
};

/**
 * Retrieve a token from sessionStorage
 */
export const getSessionToken = (key: string): string | null => {
    try {
        return sessionStorage.getItem(key);
    } catch (error) {
        console.error('[Session Storage] Failed to get token:', error);
        return null;
    }
};

/**
 * Remove a specific token from sessionStorage
 */
export const removeSessionToken = (key: string): void => {
    try {
        sessionStorage.removeItem(key);
    } catch (error) {
        console.error('[Session Storage] Failed to remove token:', error);
    }
};

/**
 * Clear all session tokens
 */
export const clearSession = (): void => {
    try {
        Object.values(TOKEN_KEYS).forEach(key => {
            sessionStorage.removeItem(key);
        });
    } catch (error) {
        console.error('[Session Storage] Failed to clear session:', error);
    }
};

/**
 * Check if a valid session exists
 */
export const hasValidSession = (): boolean => {
    try {
        const idToken = sessionStorage.getItem(TOKEN_KEYS.ID_TOKEN);
        if (!idToken) return false;

        // Check if token is expired
        const payload = JSON.parse(atob(idToken.split('.')[1]));
        const exp = payload.exp * 1000; // Convert to milliseconds
        const now = Date.now();

        // Consider valid if not expired (with 5 minute buffer)
        return exp - now > 5 * 60 * 1000;
    } catch (error) {
        console.error('[Session Storage] Failed to validate session:', error);
        return false;
    }
};

export { TOKEN_KEYS };
